import React, { useMemo } from 'react';

import { Grid } from '@mui/material';
import { FormikProps } from 'formik';
import * as Yup from 'yup';

import { GrupoUsuarioAutoComplete } from 'components/autocompletes/GrupoUsuarioAutoComplete';
import { CissTextField } from 'components/fields';
import { Row } from 'components/form';
import { FullLoading } from 'components/loading';
import { ConfirmModal } from 'components/modal';
import { Block, FormPage } from 'components/page';
import { SubTitle } from 'components/text';
import { useFormContext } from 'context/FormContext';
import { GrupoExibicaoProps } from 'pages/cadastro/loja/types';
import { useGrupoExibicaoContext } from 'pages/cadastro/secoes/contexts/GrupoExibicaoContext';
import { useMutateGrupoExibicao } from 'pages/cadastro/secoes/hooks/useMutateGrupoExibicao';

const mapContentToInitialValues = (contentData: GrupoExibicaoProps): GrupoExibicaoProps => {
    if (Array.isArray(contentData.configuracaoPropriedadeGrupoExibicaoGrupoUsuario)) {
        return {
            ...contentData,
            configuracaoPropriedadeGrupoExibicaoGrupoUsuario: contentData.configuracaoPropriedadeGrupoExibicaoGrupoUsuario.map((config) => ({
                idGrupoUsuario: config.grupoUsuario?.idGrupoUsuario ?? config.idGrupoUsuario,
                dsGrupoUsuario: config.grupoUsuario?.dsGrupoUsuario ?? config.dsGrupoUsuario,
            })),
        };
    }

    return contentData;
};

const url = '/gestao/configuracaopropriedadegrupoexibicao/';

const initialFormValues: GrupoExibicaoProps = {
    dsConfiguracaoPropriedadeGrupoExibicao: '',
    nrSequencia: '',
    fgReordena: false,
    configuracaoPropriedadeGrupoExibicaoGrupoUsuario: [],
};

export function LojaSecaoFormComp(): JSX.Element {
    const { isLoading } = useFormContext();
    const { onSubmitForm } = useMutateGrupoExibicao();
    const { openModalGrupoExibicao, setOpenModalGrupoExibicao } = useGrupoExibicaoContext();

    const validationSchemaLoja = useMemo(
        () =>
            Yup.object().shape({
                dsConfiguracaoPropriedadeGrupoExibicao: Yup.string().required('A descrição é obrigatória').nullable(),
                nrSequencia: Yup.string().required('A sequência é obrigatória').nullable(),
            }),
        [],
    );
    return (
        <FormPage
            title={'Seções'}
            values={initialFormValues}
            url={url}
            onSubmit={onSubmitForm}
            validationSchema={validationSchemaLoja}
            mapContentToInitialValues={mapContentToInitialValues}
            fgAtivo
        >
            {(formik: FormikProps<GrupoExibicaoProps>) => {
                return (
                    <>
                        <FullLoading loading={isLoading} />

                        <Block>
                            <SubTitle label="Inserir seção" />

                            <Row>
                                <Grid item flex={1}>
                                    <CissTextField
                                        label="Descrição da seção"
                                        name="dsConfiguracaoPropriedadeGrupoExibicao"
                                        value={formik.values.dsConfiguracaoPropriedadeGrupoExibicao}
                                        error={formik.errors.dsConfiguracaoPropriedadeGrupoExibicao}
                                        onChange={(e) => formik.setFieldValue('dsConfiguracaoPropriedadeGrupoExibicao', e.target.value)}
                                    />
                                </Grid>

                                <Grid item flex={1}>
                                    <CissTextField
                                        label="Ordem de apresentação"
                                        name="nrSequencia"
                                        value={formik.values.nrSequencia}
                                        error={formik.errors.nrSequencia}
                                        onChange={(e) => formik.setFieldValue('nrSequencia', e.target.value)}
                                    />
                                </Grid>
                            </Row>
                        </Block>
                        <Block>
                            <Grid item flex={1}>
                                <GrupoUsuarioAutoComplete
                                    multiple={true}
                                    value={formik.values.configuracaoPropriedadeGrupoExibicaoGrupoUsuario}
                                    onChange={(e, value) => formik.setFieldValue('configuracaoPropriedadeGrupoExibicaoGrupoUsuario', value)}
                                />
                            </Grid>
                        </Block>

                        <ConfirmModal
                            open={openModalGrupoExibicao}
                            setOpen={setOpenModalGrupoExibicao}
                            confirm={() => {
                                onSubmitForm({ ...formik.values, fgReordena: true }, formik);
                                setOpenModalGrupoExibicao((prevState) => !prevState);
                            }}
                            text="Já existe uma seção com essa ordem, deseja reordenar mesmo assim?"
                        />
                    </>
                );
            }}
        </FormPage>
    );
}
